import React, { useState } from "react";
import { Navigate } from "react-router-dom";

// Componentes da UI (shadcn/ui)
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Assets e Autenticação
import Logo from '../assets/logo2.png';
import Fundo from '../assets/fundoabstrato.png';
import { useAuth } from "../App";

const AuthPage = () => {
  const [keepConnected, setKeepConnected] = useState(true);
  const { user, login, register } = useAuth();
  const [loading, setLoading] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    name: "",
    email: "",
    password: "",
    sector: "tecnico", // Valor padrão
    role: "employee"   // Valor padrão
  });

  // Redireciona se o usuário já estiver logado
  if (user) {
    return user.role === "admin" || user.role === "coordinator" 
      ? <Navigate to="/admin" /> 
      : <Navigate to="/dashboard" />;
  }

  // Função para lidar com o login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(loginForm.email, loginForm.password);
    setLoading(false);
  };

  // Função para lidar com o registro
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await register(registerForm);
    if (result.success) {
      // Limpa o formulário após o sucesso
      setRegisterForm({ name: "", email: "", password: "", sector: "tecnico", role: "employee" });
      // Opcional: Alterne para a aba de login ou mostre uma mensagem de sucesso
    }
    setLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-stretch"
      style={{
        backgroundColor: '#1a1a1a',
        backgroundImage: `
          url(${Fundo}),
          radial-gradient(circle at 1px 1px, #333 1px, transparent 0),
          linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
        `,
        backgroundSize: 'cover, 100% 100%, 20px 20px, 20px 20px',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      }}
    >
      {/* === Header === */}
      <header className="flex justify-between items-center w-full px-8 py-4 bg-black z-10">
        <div className="flex items-center">
          <img 
            src={Logo} 
            alt="Escola de Suporti" 
            className="h-20 w-auto ml-20" 
          />
        </div>
      </header>

      {/* --- Card Centralizado com Abas --- */}
      <main className="flex flex-grow items-center justify-center w-full p-4">
        <Card className="login-card w-full max-w-md bg-white text-gray-800 shadow-2xl p-8">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-gray-200">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="register">Registrar</TabsTrigger>
            </TabsList>

            {/* === Aba de Login === */}
            <TabsContent value="login">
              <div className="text-center mb-4">
                <CardTitle className="text-3xl text-gray-800 tracking-wide font-bold">
                  SEJA BEM-VINDO!
                </CardTitle>
              </div>
              <CardContent className="p-0">
                <form onSubmit={handleLogin} className="space-y-3 flex flex-col items-center">
                  <div className="form-group w-full mb-2">
                    <Label htmlFor="login-email" className="form-label font-bold text-md text-gray-600">Usuário</Label>
                    <Input
                      id="login-email" type="email" placeholder="dr.suporti"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                      className="form-input text-lg p-5 bg-gray-100 border-gray-300"
                    />
                  </div>
                  <div className="form-group w-full">
                    <Label htmlFor="login-password" className="form-label font-bold text-md text-gray-600">Senha</Label>
                    <Input
                      id="login-password" type="password" placeholder="••••••••"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      className="form-input text-lg p-5 bg-gray-100 border-gray-300"
                    />
                  </div>
                  <div className="flex items-center space-x-2 pt-2 w-full">
                    <Checkbox
                      id="keepConnected"
                      checked={keepConnected}
                      onCheckedChange={setKeepConnected}
                      className="data-[state=checked]:bg-[#3c9f50] data-[state=checked]:border-[#3c9f50] border-gray-400 w-5 h-5"
                    />
                    <Label htmlFor="keepConnected" className="text-sm font-medium text-gray-600">Mantenha-me conectado</Label>
                  </div>
                  <Button type="submit" className="w-60 mt-8 py-3 h-auto text-lg font-bold bg-[#3c9f50] hover:bg-[#2e8b46] transition-colors" disabled={loading}>
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Iniciando...
                      </>
                    ) : "Iniciar Sessão"}
                  </Button>
                </form>
              </CardContent>
            </TabsContent>

            {/* === Aba de Cadastro === */}
            <TabsContent value="register">
               <div className="text-center mb-4">
                <CardTitle className="text-3xl text-gray-800 tracking-wide font-bold">
                  CRIE SUA CONTA
                </CardTitle>
              </div>
              <CardContent className="p-0">
                <form onSubmit={handleRegister} className="space-y-3 flex flex-col items-center">
                    <div className="form-group w-full">
                        <Label htmlFor="register-name" className="form-label font-bold text-md text-gray-600">Nome Completo</Label>
                        <Input
                            id="register-name" type="text" placeholder="Seu Nome Completo"
                            value={registerForm.name}
                            onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                            required
                            className="form-input text-lg p-5 bg-gray-100 border-gray-300"
                        />
                    </div>
                    <div className="form-group w-full">
                        <Label htmlFor="register-email" className="form-label font-bold text-md text-gray-600">Email</Label>
                        <Input
                            id="register-email" type="email" placeholder="seu.email@empresa.com"
                            value={registerForm.email}
                            onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                            required
                            className="form-input text-lg p-5 bg-gray-100 border-gray-300"
                        />
                    </div>
                     <div className="form-group w-full">
                        <Label htmlFor="register-password" className="form-label font-bold text-md text-gray-600">Senha</Label>
                        <Input
                            id="register-password" type="password" placeholder="••••••••"
                            value={registerForm.password}
                            onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                            required
                            className="form-input text-lg p-5 bg-gray-100 border-gray-300"
                        />
                    </div>
                    {/* Campos de Setor e Função podem ser adicionados aqui se necessário */}
                    <Button type="submit" className="w-60 mt-8 py-3 h-auto text-lg font-bold bg-[#3c9f50] hover:bg-[#2e8b46] transition-colors" disabled={loading}>
                        {loading ? 'Registrando...' : 'Criar Conta'}
                    </Button>
                </form>
              </CardContent>
            </TabsContent>

          </Tabs>
        </Card>
      </main>
      
      {/* --- Footer --- */}
      <footer className="text-center p-4 text-gray-400 text-xs sm:text-sm">
        Desenvolvido por Dr Suporti © 2025 Todos os direitos reservados.
      </footer>
    </div>
  );
};

export default AuthPage;



























// import React, { useState } from "react";
// import { Navigate } from "react-router-dom";
// import { Button } from "@/components/ui/button";
// import { Input } from "@/components/ui/input";
// import { Label } from "@/components/ui/label";
// import { Card, CardContent, CardTitle } from "@/components/ui/card";
// import { Checkbox } from "@/components/ui/checkbox"; 
// import Logo from '../assets/logo2.png'; // Verifique se este caminho está correto
// import Fundo from '../assets/fundoabstrato.png';
// import { useAuth } from "../App";
// import { Link } from "react-router-dom";

// const LoginPage = () => {
//   const [keepConnected, setKeepConnected] = useState(true); 



//   const { user, login, register } = useAuth();
//   const [loginForm, setLoginForm] = useState({ email: "", password: "" });
//   const [registerForm, setRegisterForm] = useState({
//     name: "",
//     email: "",
//     password: "",
//     sector: "tecnico", // Default value to prevent empty sector
//     role: "employee"
//   });
//   const [loading, setLoading] = useState(false);

//   if (user) {
//     return user.role === "admin" || user.role === "coordinator" 
//       ? <Navigate to="/admin" /> 
//       : <Navigate to="/dashboard" />;
//   }

//   const handleLogin = async (e) => {
//     e.preventDefault();
//     setLoading(true);
    
//     const result = await login(loginForm.email, loginForm.password);
    
//     setLoading(false);
//   };

//   const handleRegister = async (e) => {
//     e.preventDefault();
//     setLoading(true);
    
//     const result = await register(registerForm);
    
//     if (result.success) {
//       setRegisterForm({ name: "", email: "", password: "", sector: "tecnico", role: "employee" });
//     }
    
//     setLoading(false);
//   };


//   return (
//     <div 
//       className="min-h-screen flex flex-col items-stretch" // Ocupa toda a tela, alinha itens verticalmente
//       style={{
//         backgroundColor: '#1a1a1a',
//         backgroundImage: `
//             url(${Fundo}),
//             radial-gradient(circle at 1px 1px, #333 1px, transparent 0),
//             linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
//             linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
//         `,
//         backgroundSize: 'cover, 100% 100%, 20px 20px, 20px 20px',
//         backgroundRepeat: 'no-repeat',
//         backgroundPosition: 'center',
//       }}
//     >
//         {/* === Header/Navigation Bar - Ocupa 100% da largura === */}
//         <header className="flex justify-between items-center w-full px-8 py-4 bg-black z-10">
//             <div className="flex items-center">
//                 <img 
//                     src={Logo} 
//                     alt="Escola de Suporti" 
//                     // Ajustamos a altura da logo para 40px, como na imagem original
//                     className="h-20 w-auto ml-20" 
//                 />
//             </div>
            
            
//         </header>

//         {/* --- Card de Login Centralizado --- */}
//         {/* flex-grow permite que o main preencha o espaço restante, garantindo o centralização vertical */}
//         <main className="flex flex-grow items-center justify-center w-full p-4 ">
//             <Card className="w-full max-w-md bg-white text-gray-800 shadow-2xl  p-8">
//                 <div className="text-center mb-1 mt-4">
//                     <CardTitle className="text-3xl text-gray-800 tracking-wide font-bold">
//                         SEJA BEM-VINDO!
//                     </CardTitle>
//                 </div>
                
//                 <CardContent>
//                     <form onSubmit={handleLogin} className="space-y-3 flex flex-col items-center">
//                         {/* Campo Usuário */}
//                         <div className="form-group w-full mb-2">
//                             <Label htmlFor="login-user" className="form-label font-bold text-md text-gray-600">
//                                 Usuário
//                             </Label>
//                             <Input
//                                 id="login-email"
//                                 type="email" 
//                                 placeholder="dr.suporti"
//                                 value={loginForm.email}
//                                 onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
//                                 required
//                                 className="form-input text-lg p-5 bg-gray-100 border-gray-300"
//                             />
//                         </div>
                        
//                         {/* Campo Senha */}
//                         <div className="form-group w-full">
//                             <Label htmlFor="login-password" className="form-label font-bold text-md text-gray-600">
//                                 Senha
//                             </Label>
//                             <Input
//                                 id="login-password"
//                                 type="password"
//                                 placeholder="••••••••"
//                                 value={loginForm.password}
//                                 onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
//                                 required
//                                 className="form-input text-lg p-5 bg-gray-100 border-gray-300"
//                             />
//                         </div>
                        
//                         {/* Checkbox "Mantenha-me conectado" */}
//                         <div className="flex items-center space-x-2 pt-2 w-full">
//                             <Checkbox 
//                                 id="keepConnected" 
//                                 checked={keepConnected}
//                                 onCheckedChange={setKeepConnected}
//                                 className="data-[state=checked]:bg-[#3c9f50] data-[state=checked]:border-[#3c9f50] border-gray-400 w-5 h-5"
//                             />
//                             <Label htmlFor="keepConnected" className="text-sm font-medium text-gray-600">
//                                 Mantenha-me conectado
//                             </Label>
//                         </div>

//                         {/* Botão "Iniciar Sessão" Verde */}
//                         <Button 
//                             type="submit" 
//                             className="w-60 mt-8  py-3 h-auto text-lg font-bold bg-[#3c9f50] hover:bg-[#2e8b46] transition-colors"
//                             disabled={loading}
//                         >
//                             {loading ? (
//                                 <>
//                                     <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                                         <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                                         <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                                     </svg>
//                                     Iniciando...
//                                 </>
//                             ) : "Iniciar Sessão"}
//                         </Button>
//                     </form>
//                 </CardContent>
//             </Card>
//         </main>
        
//         {/* --- Footer --- */}
//         <footer className="text-center p-4 text-gray-400 text-xs sm:text-sm">
//             Desenvolvido por Dr Suporti © 2025 Todos os direitos reservados.
//         </footer>
//     </div>
//   );
// };

// export default LoginPage;



// import React, { useState } from "react";
// import { Navigate } from "react-router-dom";
// import { Button } from "@/components/ui/button";
// import { Input } from "@/components/ui/input";
// import { Label } from "@/components/ui/label";
// import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
// import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
// import { useAuth } from "../App";

// const LoginPage = () => {
//   const { user, login, register } = useAuth();
//   const [loginForm, setLoginForm] = useState({ email: "", password: "" });
//   const [registerForm, setRegisterForm] = useState({
//     name: "",
//     email: "",
//     password: "",
//     sector: "tecnico", // Default value to prevent empty sector
//     role: "employee"
//   });
//   const [loading, setLoading] = useState(false);

//   if (user) {
//     return user.role === "admin" || user.role === "coordinator" 
//       ? <Navigate to="/admin" /> 
//       : <Navigate to="/dashboard" />;
//   }

//   const handleLogin = async (e) => {
//     e.preventDefault();
//     setLoading(true);
    
//     const result = await login(loginForm.email, loginForm.password);
    
//     setLoading(false);
//   };

//   const handleRegister = async (e) => {
//     e.preventDefault();
//     setLoading(true);
    
//     const result = await register(registerForm);
    
//     if (result.success) {
//       setRegisterForm({ name: "", email: "", password: "", sector: "tecnico", role: "employee" });
//     }
    
//     setLoading(false);
//   };

//   return (
//     <div className="min-h-screen flex items-center justify-center p-4" style={{
//       background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
//     }}>
//       <div className="w-full max-w-md">
//         <div className="text-center mb-8">
//           <h1 className="text-4xl font-bold text-white mb-2">Escola de Suporti</h1>
//           <p className="text-blue-100">Plataforma de Treinamentos Internos</p>
//         </div>

//         <Card className="glass-card border-0">
//           <CardHeader className="text-center pb-2">
//             <CardTitle className="text-2xl font-bold text-gray-800">
//               Acesso à Plataforma
//             </CardTitle>
//             <CardDescription className="text-gray-600">
//               Entre com suas credenciais ou registre-se
//             </CardDescription>
//           </CardHeader>
          
//           <CardContent>
//             <Tabs defaultValue="login" className="w-full">
//               <TabsList className="grid w-full grid-cols-2 mb-6">
//                 <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
//                 <TabsTrigger value="register" data-testid="register-tab">Registrar</TabsTrigger>
//               </TabsList>
              
//               <TabsContent value="login">
//                 <form onSubmit={handleLogin} className="space-y-4">
//                   <div className="form-group">
//                     <Label htmlFor="login-email" className="form-label">Email</Label>
//                     <Input
//                       id="login-email"
//                       type="email"
//                       placeholder="seu.email@empresa.com"
//                       value={loginForm.email}
//                       onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
//                       required
//                       className="form-input"
//                       data-testid="login-email-input"
//                     />
//                   </div>
                  
//                   <div className="form-group">
//                     <Label htmlFor="login-password" className="form-label">Senha</Label>
//                     <Input
//                       id="login-password"
//                       type="password"
//                       placeholder="••••••••"
//                       value={loginForm.password}
//                       onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
//                       required
//                       className="form-input"
//                       data-testid="login-password-input"
//                     />
//                   </div>
                  
//                   <Button 
//                     type="submit" 
//                     className="btn btn-primary w-full mt-6"
//                     disabled={loading}
//                     data-testid="login-submit-btn"
//                   >
//                     {loading ? (
//                       <div className="loading-spinner mr-2" />
//                     ) : null}
//                     {loading ? "Entrando..." : "Entrar"}
//                   </Button>
//                 </form>
//               </TabsContent>
              
//               <TabsContent value="register">
//                 <form onSubmit={handleRegister} className="space-y-4">
//                   <div className="form-group">
//                     <Label htmlFor="register-name" className="form-label">Nome Completo</Label>
//                     <Input
//                       id="register-name"
//                       type="text"
//                       placeholder="Seu Nome Completo"
//                       value={registerForm.name}
//                       onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
//                       required
//                       className="form-input"
//                       data-testid="register-name-input"
//                     />
//                   </div>
                  
//                   <div className="form-group">
//                     <Label htmlFor="register-email" className="form-label">Email</Label>
//                     <Input
//                       id="register-email"
//                       type="email"
//                       placeholder="seu.email@empresa.com"
//                       value={registerForm.email}
//                       onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
//                       required
//                       className="form-input"
//                       data-testid="register-email-input"
//                     />
//                   </div>
                  
//                   <div className="form-group">
//                     <Label htmlFor="register-sector" className="form-label">Setor</Label>
//                     <Select 
//                       value={registerForm.sector}
//                       onValueChange={(value) => setRegisterForm({ ...registerForm, sector: value })}
//                       data-testid="register-sector-select"
//                     >
//                       <SelectTrigger className="form-input">
//                         <SelectValue placeholder="Selecione seu setor" />
//                       </SelectTrigger>
//                       <SelectContent>
//                         <SelectItem value="estagio">Estágio</SelectItem>
//                         <SelectItem value="tecnico">Técnico</SelectItem>
//                         <SelectItem value="comercial">Comercial</SelectItem>
//                       </SelectContent>
//                     </Select>
//                   </div>
                  
//                   <div className="form-group">
//                     <Label htmlFor="register-role" className="form-label">Função</Label>
//                     <Select 
//                       value={registerForm.role}
//                       onValueChange={(value) => setRegisterForm({ ...registerForm, role: value })}
//                       data-testid="register-role-select"
//                     >
//                       <SelectTrigger className="form-input">
//                         <SelectValue placeholder="Selecione sua função" />
//                       </SelectTrigger>
//                       <SelectContent>
//                         <SelectItem value="employee">Colaborador</SelectItem>
//                         <SelectItem value="coordinator">Coordenador</SelectItem>
//                         <SelectItem value="admin">Administrador</SelectItem>
//                       </SelectContent>
//                     </Select>
//                   </div>
                  
//                   <div className="form-group">
//                     <Label htmlFor="register-password" className="form-label">Senha</Label>
//                     <Input
//                       id="register-password"
//                       type="password"
//                       placeholder="••••••••"
//                       value={registerForm.password}
//                       onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
//                       required
//                       className="form-input"
//                       data-testid="register-password-input"
//                     />
//                   </div>
                  
//                   <Button 
//                     type="submit" 
//                     className="btn btn-primary w-full mt-6"
//                     disabled={loading}
//                     data-testid="register-submit-btn"
//                   >
//                     {loading ? (
//                       <div className="loading-spinner mr-2" />
//                     ) : null}
//                     {loading ? "Registrando..." : "Registrar"}
//                   </Button>
//                 </form>
//               </TabsContent>
//             </Tabs>
//           </CardContent>
//         </Card>
        
//         <div className="text-center mt-6">
//           <p className="text-blue-100 text-sm">
//             Plataforma para treinamentos internos da empresa
//           </p>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default LoginPage;